import { Component, OnInit, Inject, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import moment from 'moment';
import * as XLSX from 'xlsx';
import { removeBlankStringCells } from '../../shared/utils/excel.utils';
import {
  VacationService,
  VacationEmployee,
  VacationParams,
} from '../../core/services/vacation.service';
import { DateHolidayService, WeekDayInfo } from '../../core/services/date-holiday.service';

interface WeekDay {
  header: moment.Moment;
  redDay: string | undefined;
  weekIndex: number;
  weekNumber: number;
  semester: boolean;
  parentalLeave: boolean;
}

interface VacationWeekHeader {
  weekNumber: number;
  dateRange: string;
}

interface VacationEmployeeWithStructure extends VacationEmployee {
  manglat?: WeekDay[][]; // Week groups with days
}

@Component({
  selector: 'app-ng-vacation',
  template: require('./ng-vacation.component.html').default,
  styles: [require('!!raw-loader!sass-loader!./ng-vacation.component.scss').default],
  standalone: false,
})
export class NgVacationComponent implements OnInit {
  // Date range
  dateFrom: moment.Moment | null = null;
  dateTo: moment.Moment | null = null;

  // Data
  vacationData: VacationEmployeeWithStructure[] = [];
  weekListMoment: WeekDayInfo[] = [];
  orgStruct: WeekDay[][] = []; // Week groups with days
  header: (string | number)[] = [];
  weekHeaders: VacationWeekHeader[] = [];

  // UI
  exportData: any[][] = [];

  private destroyRef = inject(DestroyRef);

  constructor(
    @Inject(VacationService) private vacationService: VacationService,
    @Inject(DateHolidayService) private dateHolidayService: DateHolidayService
  ) {}

  ngOnInit(): void {
    this.initializeDates();
  }

  private initializeDates(): void {
    // Initialize dateFrom to the start of the current week (Monday)
    this.dateFrom = moment().startOf('isoWeek');

    // Initialize dateTo to 12 weeks forward, end of the week (Sunday)
    this.dateTo = moment(this.dateFrom).add(11, 'weeks').endOf('isoWeek');
  }

  updatePeriod(direction: number): void {
    if (!this.dateFrom || !this.dateTo) return;

    if (direction === -1) {
      // Go back 1 week
      this.dateFrom = moment(this.dateFrom).subtract(1, 'weeks');
      this.dateTo = moment(this.dateTo).subtract(1, 'weeks');
    } else {
      // Go forward 1 week
      this.dateFrom = moment(this.dateFrom).add(1, 'weeks');
      this.dateTo = moment(this.dateTo).add(1, 'weeks');
    }

    // Align to week boundaries (just to be safe)
    this.dateFrom.startOf('isoWeek');
    this.dateTo.endOf('isoWeek');
  }

  fetchVacation(): void {
    if (!this.dateFrom || !this.dateTo) return;

    this.vacationData = [];
    this.exportData = [];

    // Setup headers
    this.setupHeaders();

    // Get week list from DateHolidayService
    this.weekListMoment = this.dateHolidayService.getWeekListDates(
      this.dateFrom.clone(),
      this.dateTo.clone()
    );

    // Process header data (add week numbers)
    this.processHeaderData(this.weekListMoment);

    // Build week structure
    this.buildWeekStructure();

    // Fetch vacation data from API
    const params: VacationParams = {
      from: this.dateFrom.format('YYYY-MM-DD'),
      to: this.dateTo.format('YYYY-MM-DD'),
    };

    this.vacationService
      .get(params)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.processVacationData(data);
        },
        error: (error) => {
          console.error('Error fetching vacation:', error);
        },
      });
  }

  private setupHeaders(): void {
    // Start with employee name column
    this.header = ['NPC'];
    this.weekHeaders = [];
  }

  private processHeaderData(data: WeekDayInfo[]): void {
    const weeks: WeekDayInfo[][] = [];
    let currentWeek: WeekDayInfo[] = [];

    for (const day of data) {
      if (this.dateHolidayService.isWeekendDay(day.header)) {
        continue;
      }

      if (currentWeek.length > 0 && day.weekNumber !== currentWeek[0].weekNumber) {
        weeks.push(currentWeek);
        currentWeek = [];
      }

      currentWeek.push(day);
    }

    if (currentWeek.length > 0) {
      weeks.push(currentWeek);
    }

    this.weekHeaders = weeks.map((week) => ({
      weekNumber: week[0].weekNumber,
      dateRange: `${week[0].header.format('D/M')} - ${week[week.length - 1].header.format('D/M')}`,
    }));

    this.header.push(...this.weekHeaders.map((week) => week.weekNumber));
  }

  private buildWeekStructure(): void {
    this.orgStruct = [];
    let lastWeekNumber: number | null = null;
    let currentWeekGroup: WeekDay[] | null = null;

    for (const weekDayElement of this.weekListMoment) {
      // Skip weekends (Saturday and Sunday)
      if (this.dateHolidayService.isWeekendDay(weekDayElement.header)) {
        continue;
      }

      if (weekDayElement.weekNumber !== lastWeekNumber) {
        lastWeekNumber = weekDayElement.weekNumber;
        currentWeekGroup = [];
        this.orgStruct.push(currentWeekGroup);
      }

      currentWeekGroup!.push({
        header: weekDayElement.header,
        redDay: weekDayElement.redDay,
        weekIndex: weekDayElement.weekIndex,
        weekNumber: weekDayElement.weekNumber,
        semester: false,
        parentalLeave: false,
      });
    }
  }

  private processVacationData(data: VacationEmployee[]): void {
    // For each employee, clone orgStruct and mark vacation and parental leave dates
    this.vacationData = data.map((employee) => {
      // Deep clone orgStruct for this employee
      const manglat: WeekDay[][] = this.orgStruct.map((week) => week.map((day) => ({ ...day })));

      // Mark vacation dates
      employee.vacation_dates.forEach((vacationDate) => {
        for (const week of manglat) {
          for (const day of week) {
            if (day.header.format('YYYY-MM-DD') === vacationDate) {
              day.semester = true;
            }
          }
        }
      });

      // Mark parental leave dates
      employee.parental_leave_dates.forEach((parentalDate) => {
        for (const week of manglat) {
          for (const day of week) {
            if (day.header.format('YYYY-MM-DD') === parentalDate) {
              day.parentalLeave = true;
            }
          }
        }
      });

      return {
        ...employee,
        manglat,
      };
    });
  }

  getDayTitle(day: WeekDay): string {
    const dateStr = day.header.format('YYYY-MM-DD');
    const labels: string[] = [];
    if (day.semester) {
      labels.push('Semester');
    }
    if (day.parentalLeave) {
      labels.push('Föräldraledig');
    }
    if (day.redDay) {
      labels.push(day.redDay);
    }
    return labels.length > 0 ? `${dateStr} (${labels.join(', ')})` : dateStr;
  }

  exportToExcel(): void {
    if (this.vacationData.length === 0) {
      return;
    }

    // Build export data
    const exportData: any[][] = [];

    // Add header row with two columns per week: Sem and Föräldr
    const headerRow: string[] = ['NPC'];
    this.weekHeaders.forEach((week) => {
      headerRow.push(`V ${week.weekNumber} Sem`);
      headerRow.push(`V ${week.weekNumber} Föräldr`);
    });
    exportData.push(headerRow);

    // Add data rows
    this.vacationData.forEach((vacay) => {
      const row: any[] = [vacay.name];

      // For each week, count vacation and parental leave days separately
      if (vacay.manglat) {
        vacay.manglat.forEach((week) => {
          const vacationDays = week.filter((day) => day.semester).length;
          const parentalDays = week.filter((day) => day.parentalLeave).length;
          row.push(vacationDays > 0 ? vacationDays : '');
          row.push(parentalDays > 0 ? parentalDays : '');
        });
      }

      exportData.push(row);
    });

    // Create workbook
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(exportData);
    removeBlankStringCells(ws);

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Semester & Föräldraledighet');

    // Save file
    XLSX.writeFile(wb, 'semester_foraldraledighet.xlsx', { bookType: 'xlsx' });
  }

  // TrackBy functions for performance
  trackByIndex(index: number): number {
    return index;
  }

  trackByName(index: number, item: VacationEmployeeWithStructure): string {
    return item.name;
  }
}
